import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function sameApplicationServerKey(currentKey, expectedKey) {
  if (!currentKey) return true;
  const current = new Uint8Array(currentKey);
  if (current.length !== expectedKey.length) return false;
  return current.every((value, index) => value === expectedKey[index]);
}

// Echte Web-Push-Subscription (funktioniert auch bei geschlossener/installierter PWA).
export async function ensurePushSubscription() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return;
    const reg = await navigator.serviceWorker.ready;
    const { data } = await api.get("/push/public-key");
    if (!data.key) return;
    const applicationServerKey = urlBase64ToUint8Array(data.key);
    let sub = await reg.pushManager.getSubscription();
    if (sub && !sameApplicationServerKey(sub.options?.applicationServerKey, applicationServerKey)) {
      try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch { /* Neue Registrierung trotzdem versuchen. */ }
      await sub.unsubscribe();
      sub = null;
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }
    await api.post("/push/subscribe", sub.toJSON());
  } catch {
    // Push nicht verfügbar (z.B. iOS ohne Homescreen-Installation) — In-App-Badge greift trotzdem
  }
}

// Roter Zähler auf dem Favicon (Browser-Tab) + "(N)" im Seitentitel.
let faviconOriginals = null;
let baseTitle = null;
function updateTabBadge(count) {
  try {
    if (baseTitle === null) baseTitle = document.title;
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
    const links = document.querySelectorAll('link[rel="icon"]');
    if (!links.length) return;
    if (faviconOriginals === null) faviconOriginals = [...links].map((l) => l.href);
    if (count <= 0) {
      links.forEach((l, i) => { l.href = faviconOriginals[i]; });
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = faviconOriginals[0];
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = c.height = 64;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, 64, 64);
      ctx.beginPath();
      ctx.arc(45, 19, 18, 0, 2 * Math.PI);
      ctx.fillStyle = "#ef4444";
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 26px -apple-system, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(count > 9 ? "9+" : String(count), 45, 21);
      const url = c.toDataURL("image/png");
      links.forEach((l) => { l.href = url; });
    };
  } catch {}
}

// Einmal pro Sitzung freundlich nach Benachrichtigungs-Erlaubnis fragen.
let optInPrompted = false;
function maybePromptNotifications() {
  if (optInPrompted) return;
  if (!("Notification" in window) || !("PushManager" in window)) return;
  if (Notification.permission !== "default") return;
  const dismissed = Number(localStorage.getItem("push-optin-dismissed") || 0);
  if (dismissed && Date.now() - dismissed < 7 * 24 * 3600 * 1000) return;
  optInPrompted = true;
  toast("🔔 Benachrichtigungen aktivieren?", {
    description: "Aufgaben, Chat & Freigaben direkt aufs Gerät — wie bei WhatsApp, auch bei geschlossener App.",
    duration: 8000,
    action: {
      label: "Aktivieren",
      onClick: () => ensurePushSubscription(),
    },
    onDismiss: () => localStorage.setItem("push-optin-dismissed", String(Date.now())),
  });
}

export function useChatUnread(suppressed) {
  const [unread, setUnread] = useState(0);
  const prevRef = useRef(null);
  const supRef = useRef(suppressed);
  supRef.current = suppressed;

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "granted") ensurePushSubscription();
    else maybePromptNotifications();
    let cancelled = false;
    const poll = () => {
      api
        .get("/chat/unread")
        .then(({ data }) => {
          if (cancelled) return;
          const total = data.total || 0;
          // App-Icon-Badge (Homescreen) synchron halten
          try {
            if ("setAppBadge" in navigator) {
              if (total > 0) navigator.setAppBadge(total);
              else navigator.clearAppBadge();
            }
          } catch {}
          // Favicon + Tab-Titel (Web-Variante)
          updateTabBadge(supRef.current ? 0 : total);
          if (prevRef.current !== null && total > prevRef.current && !supRef.current) {
            const diff = total - prevRef.current;
            toast.info(diff === 1 ? "💬 Neue Chat-Nachricht" : `💬 ${diff} neue Chat-Nachrichten`);
          }
          prevRef.current = total;
          setUnread(supRef.current ? 0 : total);
        })
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 20000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    if (suppressed) {
      setUnread(0);
      prevRef.current = 0;
      updateTabBadge(0);
    }
  }, [suppressed]);

  return unread;
}

export function useConversations(enabled = true) {
  const [convs, setConvs] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const load = () =>
      api
        .get("/chat/conversations")
        .then(({ data }) => !cancelled && setConvs(data))
        .catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [enabled]);
  return [convs, setConvs];
}
